import { HttpErrorResponse } from '@angular/common/http';

type ApiErrorBody = {
  error?: string;
  message?: string;
  code?: string;
  errors?: string[];
};

/** Libellés FR pour codes métier auth (évite d'afficher SSO_REQUIRED brut dans SweetAlert). */
const AUTH_ERROR_CODE_LABELS: Readonly<Record<string, string>> = {
  SSO_REQUIRED:
    'Votre établissement utilise la connexion SSO. Connectez-vous via SSO.',
  SSO_PASSWORD_DISABLED:
    'Ce compte n’a pas de mot de passe local. Connectez-vous via SSO.',
  SOCIAL_LOGIN_REQUIRED:
    'Ce compte n’a pas de mot de passe local. Connectez-vous avec Google ou Microsoft.',
  SSO_USER_NOT_FOUND:
    'Aucun compte OGESTE pour cet email. Demandez à un administrateur de créer votre compte avant la connexion SSO.',
  SSO_ROLE_FORBIDDEN: 'Votre profil n’est pas autorisé pour cette connexion SSO.',
  SSO_EHPAD_MISMATCH: 'Ce compte n’appartient pas à l’établissement SSO détecté.',
  SSO_USER_DISABLED: 'Compte utilisateur désactivé.',
  SSO_EHPAD_DISABLED: 'Établissement désactivé.',
  SSO_SUBJECT_MISMATCH: 'Ce compte OGESTE est déjà lié à une autre identité SSO.',
  SSO_SUBJECT_ALREADY_LINKED: 'Cette identité SSO est déjà liée à un autre compte.',
  SOCIAL_USER_NOT_FOUND:
    'Aucun compte OGESTE pour cet email. Demandez à un administrateur de créer votre compte avant la connexion sociale.',
  SOCIAL_ROLE_FORBIDDEN:
    'La connexion Google / Microsoft est réservée aux intervenants externes et aux familles.',
  SOCIAL_USER_DISABLED: 'Compte utilisateur désactivé.',
  SOCIAL_PROVIDER_CONFLICT: 'Ce compte est déjà lié à un autre fournisseur social.',
  SOCIAL_SUBJECT_MISMATCH: 'Ce compte OGESTE est déjà lié à une autre identité sociale.',
  SOCIAL_SUBJECT_ALREADY_LINKED: 'Cette identité sociale est déjà liée à un autre compte.',
  SOCIAL_TICKET_INVALID: 'Ticket de connexion invalide ou expiré. Veuillez réessayer.',
  SOCIAL_PROVIDER_DISABLED: 'Ce mode de connexion n’est pas disponible.',
  SOCIAL_PROVIDER_INVALID: 'Fournisseur de connexion invalide.'
};

/** Code métier renvoyé par l'API (ex. SSO_REQUIRED). */
export function getApiErrorCode(
  error: Pick<HttpErrorResponse, 'error'> | null | undefined
): string | null {
  const body = error?.error;
  if (body && typeof body === 'object' && typeof (body as ApiErrorBody).code === 'string') {
    const code = String((body as ApiErrorBody).code).trim();
    return code || null;
  }
  return null;
}

/** SSO établissement exclusif — parcours MDP local refusés. */
export function isSsoRequiredError(
  error: Pick<HttpErrorResponse, 'error' | 'status'> | null | undefined
): boolean {
  return error?.status === 403 && getApiErrorCode(error) === 'SSO_REQUIRED';
}

/** Compte lié Google / Microsoft sans MDP local — connexion sociale requise. */
export function isSocialLoginRequiredError(
  error: Pick<HttpErrorResponse, 'error' | 'status'> | null | undefined
): boolean {
  return error?.status === 403 && getApiErrorCode(error) === 'SOCIAL_LOGIN_REQUIRED';
}

/** True si la chaîne ressemble à un code technique (SSO_REQUIRED, email_domain_mismatch…). */
export function looksLikeErrorCode(value: string): boolean {
  const v = value.trim();
  if (!v || v.includes(' ')) {
    return false;
  }
  return /^[A-Z][A-Z0-9_]+$/.test(v) || /^[a-z][a-z0-9_]+$/.test(v);
}

export function humanizeAuthErrorCode(code: string | null | undefined): string | null {
  if (!code) {
    return null;
  }
  return AUTH_ERROR_CODE_LABELS[code] || null;
}

/**
 * Extrait un message d'erreur lisible depuis une réponse API backend
 * (validateBody Joi : `error` ou `message` + tableau `errors`).
 * Remplace un code brut (SSO_REQUIRED, …) par un libellé FR connu.
 */
export function extractApiErrorMessage(
  error: Pick<HttpErrorResponse, 'error' | 'message'> | null | undefined,
  fallback = 'Une erreur est survenue. Veuillez réessayer.'
): string {
  if (!error) {
    return fallback;
  }

  const body = error.error as ApiErrorBody | string | null | undefined;
  let raw: string | null = null;

  if (typeof body === 'string' && body.trim()) {
    raw = body.trim();
  } else if (body && typeof body === 'object') {
    if (Array.isArray(body.errors)) {
      const first = body.errors.find((entry) => typeof entry === 'string' && entry.trim());
      if (first) {
        raw = first.trim();
      }
    }
    if (!raw && typeof body.error === 'string' && body.error.trim()) {
      raw = body.error.trim();
    }
    if (!raw && typeof body.message === 'string' && body.message.trim()) {
      raw = body.message.trim();
    }
  }

  if (!raw && typeof error.message === 'string' && error.message.trim()) {
    // HttpErrorResponse.message générique ("Http failure…") → ignorer
    const httpMsg = error.message.trim();
    if (!httpMsg.startsWith('Http failure')) {
      raw = httpMsg;
    }
  }

  const code = getApiErrorCode(error);
  const fromCode = humanizeAuthErrorCode(code);

  if (raw) {
    if (looksLikeErrorCode(raw)) {
      return humanizeAuthErrorCode(raw) || fromCode || fallback;
    }
    return raw;
  }

  return fromCode || fallback;
}
